/**
 * SLOTTING DASHBOARD
 *
 * View and manage slotting suggestions for optimal bin placement.
 * Shows velocity-based recommendations to move fast-moving items to accessible locations.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Banner,
  EmptyState,
  Spinner,
  Modal,
  Select,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useSlottingGeneration } from './useSlottingGeneration';

interface SlottingSuggestion {
  id: number;
  product_id: number;
  product_sku: string;
  product_title: string;
  current_location_id: number | null;
  current_location_name: string | null;
  suggested_location_id: number;
  suggested_location_name: string;
  reason: string;
  score: number;
  status: string;
  created_at: string;
}

interface VelocityStats {
  total_products: number;
  fast_moving: number;
  medium_moving: number;
  slow_moving: number;
  last_calculated: string | null;
}

interface SuggestionsResponse {
  suggestions: SlottingSuggestion[];
  total: number;
  nextCursor: string | null;
}

interface StatsResponse {
  stats: VelocityStats;
}

export default function SlottingDashboard() {
  const { fetchApi, loading, error } = useApi();
  const navigate = useNavigate(), { user, hasPermission } = useAuth();
  const [suggestions, setSuggestions] = useState<SlottingSuggestion[]>([]);
  const [stats, setStats] = useState<VelocityStats | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<SlottingSuggestion | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [recalculating, setRecalculating] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [cursors, setCursors] = useState(['']);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const cursor = cursors[cursors.length - 1];
  const updated = useCallback(() => setRefresh(n => n + 1), []);
  const generation = useSlottingGeneration(fetchApi, String(user?.id || ''), updated);
  const suggestionRead = useRef(0);

  useEffect(() => {
    setSuggestions([]); setStats(null); setSelectedSuggestion(null); setConfirmModalOpen(false);
    setCursors(['']); setNextCursor(null); setTotal(0);
    return () => { suggestionRead.current++; };
  }, [fetchApi]);

  const loadSuggestions = useCallback(async () => {
    const read = ++suggestionRead.current;
    const params = new URLSearchParams({ status: statusFilter, cursor, expected_user_id: String(user?.id || '') });
    const data = await fetchApi<SuggestionsResponse>(`/slotting/suggestions?${params}`);
    if (data?.suggestions && read === suggestionRead.current) {
      setSuggestions(data.suggestions);
      setNextCursor(data.nextCursor); setTotal(data.total);
    }
  }, [fetchApi, statusFilter, cursor, user?.id]);

  const loadStats = useCallback(async () => {
    const data = await fetchApi<StatsResponse>('/slotting/stats?expected_user_id=' + encodeURIComponent(String(user?.id || '')));
    if (data?.stats) {
      setStats(data.stats);
    }
  }, [fetchApi, user?.id]);

  useEffect(() => {
    loadSuggestions();
    loadStats();
  }, [loadSuggestions, loadStats, refresh]);

  const handleRecalculateVelocity = useCallback(async () => {
    setRecalculating(true);
    const result = await fetchApi('/slotting/velocity/recalculate', { method: 'POST', body: JSON.stringify({ expected_user_id: String(user?.id || '') }) });
    if (result) {
      loadStats();
    }
    setRecalculating(false);
  }, [fetchApi, loadStats, user?.id]);

  const handleRejectSuggestion = useCallback(async (suggestion: SlottingSuggestion) => {
    setSelectedSuggestion(suggestion);
    setConfirmModalOpen(true);
  }, []);

  const handleConfirmAction = useCallback(async () => {
    if (!selectedSuggestion) return;

    const result = await fetchApi(`/api/inventory/bin-moves/slotting/${selectedSuggestion.id}/reject`, {
      method: 'POST', body: JSON.stringify({ expected_user_id: String(user?.id || ''), checked: true }),
    });
    if (result) {
      setConfirmModalOpen(false);
      setSelectedSuggestion(null);
      setRefresh(n => n + 1);
    }
  }, [fetchApi, selectedSuggestion, user?.id]);

  const getReasonBadge = (reason: string) => {
    switch (reason) {
      case 'velocity_mismatch':
        return <Badge tone="warning">Velocity Mismatch</Badge>;
      case 'consolidation':
        return <Badge tone="info">Consolidation</Badge>;
      case 'overflow':
        return <Badge tone="attention">Overflow</Badge>;
      default:
        return <Badge>{reason}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge tone="attention">Pending</Badge>;
      case 'accepted':
        return <Badge tone="success">Accepted</Badge>;
      case 'rejected':
        return <Badge>Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading && suggestions.length === 0 && !generation.running) {
    return (
      <Page title="Slotting Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading slotting data...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Slotting Dashboard"
      subtitle="Suggest storage bins using dispatched units from the last 30 days, within each warehouse"
      primaryAction={{
        content: generation.progress.cursor ? 'Continue suggestion scan' : 'Generate Suggestions',
        onAction: generation.run,
        loading: generation.running,
        disabled: !hasPermission('inventory.edit'),
      }}
      secondaryActions={[
        {
          content: 'Refresh dispatch activity',
          onAction: handleRecalculateVelocity,
          loading: recalculating,
          disabled: !hasPermission('inventory.edit') || generation.running,
        },
        ...(generation.running ? [{ content: 'Pause suggestion scan', onAction: generation.stop }] : []),
      ]}
    >
      <Layout>
        {(generation.progress.evaluated > 0 || generation.error) && <Layout.Section><Banner tone={generation.error ? 'warning' : 'info'}><p>Checked {generation.progress.evaluated} stock rows. Confirmed {generation.progress.created} new suggestions. {generation.progress.done ? 'Scan complete.' : 'Continue the scan to cover the remaining bins.'}</p>{generation.error && <p>{generation.error}</p>}</Banner></Layout.Section>}
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Velocity Stats */}
        {stats && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Velocity Overview</Text>
                <Text as="p" tone="subdued">Each product is counted once, using its busiest warehouse. Fast: over 50 dispatched units. Medium: 21–50. Slow: 0–20.</Text>
                <InlineStack gap="800" wrap>
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="bold" variant="headingLg">
                      {stats.total_products}
                    </Text>
                    <Text as="p" tone="subdued">Total Products</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="bold" variant="headingLg" tone="success">
                      {stats.fast_moving}
                    </Text>
                    <Text as="p" tone="subdued">Fast Moving</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="bold" variant="headingLg" tone="caution">
                      {stats.medium_moving}
                    </Text>
                    <Text as="p" tone="subdued">Medium Moving</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="bold" variant="headingLg">
                      {stats.slow_moving}
                    </Text>
                    <Text as="p" tone="subdued">Slow Moving</Text>
                  </BlockStack>
                </InlineStack>
                {stats.last_calculated && (
                  <Text as="p" tone="subdued">
                    Calculated at: {new Date(stats.last_calculated).toLocaleString()}
                  </Text>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Filter */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="end">
              <div style={{ width: '200px' }}>
                <Select
                  label="Status"
                  options={[
                    { label: 'Pending', value: 'pending' },
                    { label: 'Accepted', value: 'accepted' },
                    { label: 'Rejected', value: 'rejected' },
                    { label: 'All, including expired', value: 'all' },
                  ]}
                  value={statusFilter}
                  onChange={(val) => {
                    setStatusFilter(val);
                    setCursors(['']);
                  }}
                />
              </div>
              <Button onClick={loadSuggestions} loading={loading}>
                Refresh
              </Button>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Suggestions Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Slotting Suggestions</Text>
              <Text as="p" tone="subdued">{total} matching suggestions. Scores rank placement priority; they are not percentages.</Text>
              {suggestions.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text']}
                  headings={['SKU', 'Product', 'Current Location', 'Suggested Location', 'Reason', 'Score', 'Actions']}
                  rows={suggestions.map((suggestion) => [
                    suggestion.product_sku || '-',
                    suggestion.product_title.length > 40
                      ? suggestion.product_title.substring(0, 40) + '...'
                      : suggestion.product_title,
                    suggestion.current_location_name || 'No location',
                    suggestion.suggested_location_name,
                    getReasonBadge(suggestion.reason),
                    <Text as="span" key={`score-${suggestion.id}`}>{Number(suggestion.score).toFixed(0)}</Text>,
                    suggestion.status === 'pending' ? (
                      <InlineStack gap="200" key={`actions-${suggestion.id}`}>
                        <Button
                          size="slim"
                          tone="success"
                          disabled={!hasPermission('inventory.transfer')}
                          onClick={() => navigate('/bin-moves?slotting=' + suggestion.id)}
                        >
                          Review move
                        </Button>
                        <Button
                          size="slim"
                          disabled={!hasPermission('inventory.edit')}
                          onClick={() => handleRejectSuggestion(suggestion)}
                        >
                          Reject
                        </Button>
                      </InlineStack>
                    ) : (
                      <InlineStack gap="200" key={`history-${suggestion.id}`}>{getStatusBadge(suggestion.status)}<Button size="slim" disabled={!hasPermission('inventory.transfer')} onClick={() => navigate('/bin-moves?slotting=' + suggestion.id)}>View history</Button></InlineStack>
                    ),
                  ])}
                />
              ) : (
                <EmptyState
                  heading="No slotting suggestions"
                  action={{
                    content: generation.progress.cursor ? 'Continue suggestion scan' : 'Generate Suggestions',
                    onAction: generation.run,
                    disabled: generation.running || !hasPermission('inventory.edit'),
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Generate slotting suggestions to optimize your warehouse layout
                    based on product velocity and pick frequency.
                  </p>
                </EmptyState>
              )}
              <InlineStack gap="200"><Button disabled={loading || cursors.length < 2} onClick={() => setCursors(p => p.slice(0, -1))}>Previous suggestions</Button><Button disabled={loading || !nextCursor} onClick={() => setCursors(p => [...p, nextCursor!])}>More suggestions</Button></InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Confirmation Modal */}
      <Modal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        title="Reject Suggestion"
        primaryAction={{
          content: 'Reject',
          onAction: handleConfirmAction,
          loading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setConfirmModalOpen(false) },
        ]}
      >
        <Modal.Section>
          {selectedSuggestion && (
            <BlockStack gap="400">
              <Text as="p">
                Reject the remaining suggestion for <strong>{selectedSuggestion.product_sku}</strong>?
                Any moves already saved will remain in its history. This does not move or return goods.
              </Text>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
